import {H2} from '@jetbrains/ring-ui/components/heading/heading'
import React, {useEffect, useMemo} from 'react'
import {graphql} from 'react-relay'

import {useRefetchableLazyLoadQuery} from '../../../../../hooks/relay/useRefetchableLazyLoadQuery'
import {emptyArray} from '../../../../../utils/empty'
import {subscribeOnProjectEvents} from '../../../../../utils/subscriber'
import {
  BUILD_FINISHED,
  BUILD_INTERRUPTED,
  BUILD_STARTED,
  BUILD_TYPE_ADDED_TO_QUEUE,
  BUILD_TYPE_REMOVED_FROM_QUEUE,
} from '../../../../../utils/subscriptionEvents'
import {formatBuildNumber} from '../../../../common/BuildNumber/BuildNumber.utils'
import {PipelineChart} from '../../components/common/PipelineChart/PipelineChart'
import {usePipelineGraph} from '../../EditPipelinePage/EditPipelinePage.hooks'
import type {pipelineHeadFragment$key} from '../../hooks/__generated__/pipelineHeadFragment.graphql'
import {usePipelineId} from '../../hooks/pipeline'
import {pipelinesApi} from '../../services/pipelinesApi'
import {createGraph} from '../../utils/graph'
import {useLastRunLocator} from '../PipelinePage.hooks'

import type {PipelineLastRunQuery} from './__generated__/PipelineLastRunQuery.graphql'
import PipelineLastRunJob from './PipelineLastRunJob/PipelineLastRunJob'
import PipelineStructureJob from './PipelineStructureJob/PipelineStructureJob'

import styles from './PipelineLastRun.css'

const NODE_HEIGHT = 46
const NODE_WIDTH = 204
type Props = {pipelineHeadKey: pipelineHeadFragment$key | null}

const query = graphql`
  query PipelineLastRunQuery($locator: String!) {
    builds(locator: $locator) {
      build {
        rawId
        number
        state
        snapshotDependencies {
          build {
            rawId
            buildType {
              project {
                internalId
              }
            }
            snapshotDependencies {
              build {
                rawId
              }
            }
            ...PipelineLastRunJobFragment
          }
        }
      }
    }
  }
`

export default function PipelineLastRun({pipelineHeadKey}: Props) {
  const locator = useLastRunLocator(pipelineHeadKey)
  const [{builds}, refetch] = useRefetchableLazyLoadQuery<PipelineLastRunQuery>(query, {
    locator,
  })
  const {rawId: lastRunId, number, state, snapshotDependencies} = builds?.build?.[0] ?? {}
  const dependencies = snapshotDependencies?.build
  const isEmpty = builds != null && !builds?.build?.length
  const pipelineId = usePipelineId()
  const childInternalId = dependencies?.[0]?.buildType?.project?.internalId
  useEffect(
    () =>
      childInternalId != null
        ? subscribeOnProjectEvents(
            childInternalId,
            [
              BUILD_TYPE_ADDED_TO_QUEUE,
              BUILD_TYPE_REMOVED_FROM_QUEUE,
              BUILD_STARTED,
              BUILD_FINISHED,
              BUILD_INTERRUPTED,
            ],
            refetch,
          )
        : undefined,
    [childInternalId, refetch],
  )
  const {jobs} = pipelinesApi.endpoints.getPipelineById.useQuery(pipelineId, {
    selectFromResult: ({data}) => ({
      jobs: data?.settings.jobs,
    }),
  })
  const configuredGraph = usePipelineGraph(jobs)
  const graph = useMemo(
    () =>
      dependencies &&
      createGraph({
        data: dependencies,
        getId: build => String(build.rawId),
        getDependencies: build =>
          build.snapshotDependencies?.build?.map(dep => String(dep.rawId)) ?? emptyArray,
      }),
    [dependencies],
  )

  const isReady = isEmpty ? configuredGraph != null : lastRunId != null

  return isReady ? (
    <div className={styles.lastRunWrapper}>
      <div className={styles.lastRun}>
        {!isEmpty && state !== 'queued' && (
          <H2 className={styles.lastRunTitle}>{formatBuildNumber({number}).text}</H2>
        )}
        {isEmpty
          ? configuredGraph != null && (
              <PipelineChart
                className={styles.lastRunChart}
                graph={configuredGraph}
                nodeWidth={NODE_WIDTH}
                nodeHeight={NODE_HEIGHT}
                pointOffsetY={15}
                renderJob={({data, x, y}) => (
                  <PipelineStructureJob
                    x={x! - NODE_HEIGHT / 2}
                    y={y! - NODE_WIDTH / 2}
                    jobId={data.id}
                    key={data.id}
                  />
                )}
              />
            )
          : graph != null && (
              <PipelineChart
                className={styles.lastRunChart}
                graph={graph}
                nodeWidth={NODE_WIDTH}
                nodeHeight={NODE_HEIGHT}
                pointOffsetY={15}
                renderJob={({data, x, y}) => (
                  <PipelineLastRunJob
                    x={x! - NODE_HEIGHT / 2}
                    y={y! - NODE_WIDTH / 2}
                    jobKey={data.data}
                    runId={String(lastRunId)}
                    key={data.id}
                  />
                )}
              />
            )}
      </div>
    </div>
  ) : null
}
